import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowSquareOut,
  Clock,
  CurrencyDollar,
  Robot,
  Sparkle,
  Target,
  TrendUp,
  X
} from '@phosphor-icons/react';

/**
 * OpportunityDetailDrawer - Right-side slide panel for viewing opportunity details
 * Used in Org Chart context to show opportunity information without leaving the page
 */
const OpportunityDetailDrawer = ({ opportunity, isOpen, onClose, onProceedToAgent }) => {
  const navigate = useNavigate();

  if (!opportunity) return null;

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  /**
   * Format ROI value - handles various data structures
   * Patterns: number, { roi_percentage: X }, { status: X }, etc.
   * The backend returns: { status, roi_percentage, annual_savings, roi_value, ... }
   */
  const formatROI = (roi) => {
    if (roi === null || roi === undefined) return '—';
    
    // If it's a number, return directly
    if (typeof roi === 'number') {
      return `${Math.round(roi)}%`;
    }
    
    // If it's a string that looks like a number
    if (typeof roi === 'string') {
      const parsed = parseFloat(roi);
      if (!isNaN(parsed)) {
        return `${Math.round(parsed)}%`;
      }
      return roi; // Return as-is if it's already formatted
    }
    
    // If it's an object, extract the value (prioritize actual backend fields)
    if (typeof roi === 'object') {
      // Priority 1: roi_percentage (from backend ROI computer)
      if (roi.roi_percentage !== null && roi.roi_percentage !== undefined) {
        const numValue = typeof roi.roi_percentage === 'number' ? roi.roi_percentage : parseFloat(roi.roi_percentage);
        if (!isNaN(numValue)) {
          return `${Math.round(numValue)}%`;
        }
      }
      
      // Priority 2: Other possible field names
      const roiValue = roi.value ?? roi.percent ?? roi.roi_percent ?? roi.roi ?? null;
      if (roiValue !== null && roiValue !== undefined) {
        const numValue = typeof roiValue === 'number' ? roiValue : parseFloat(roiValue);
        if (!isNaN(numValue)) {
          return `${Math.round(numValue)}%`;
        }
      }
      
      // Fallback: check if object has a displayable property
      if (roi.display) return roi.display;
      if (roi.formatted) return roi.formatted;
      
      // Last resort: if status exists, show status-based label
      if (roi.status) {
        const statusLabels = {
          'excellent': 'Excellent ROI',
          'good': 'Good ROI',
          'moderate': 'Moderate ROI',
          'poor': 'Low ROI'
        };
        return statusLabels[roi.status.toLowerCase()] || roi.status;
      }
    }
    
    return '—';
  };

  // Get priority color
  const getPriorityColor = (score) => {
    if (score >= 80) return 'text-green-400 bg-green-500/20 border-green-500/30';
    if (score >= 60) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    if (score >= 40) return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
    return 'text-red-400 bg-red-500/20 border-red-500/30';
  };

  // Get impact/effort label
  const getScoreLabel = (score) => {
    if (score >= 8) return 'High';
    if (score >= 5) return 'Medium';
    return 'Low';
  };

  // Get opportunity ID (handles different field names)
  const getOpportunityId = () => {
    return opportunity.id || opportunity.opportunity_id || null;
  };

  // Handle "Proceed to Agent Design" click
  const handleProceedToAgent = () => {
    const opportunityId = getOpportunityId();
    
    // Navigate to the correct agent creation route
    // Using query params to pass context
    const params = new URLSearchParams();
    if (opportunityId) params.set('opportunity_id', opportunityId);
    if (opportunity.role_id) params.set('role_id', opportunity.role_id);
    if (opportunity.title) params.set('opportunity_title', opportunity.title);
    
    const queryString = params.toString();
    const url = `/partner-pro/ai-workforce/agents/create${queryString ? `?${queryString}` : ''}`;
    
    navigate(url);
    onClose();
    
    // Also call the callback if provided
    if (onProceedToAgent) {
      onProceedToAgent(opportunity);
    }
  };

  // Handle "Open in Opportunities" click - deep link to specific opportunity
  const handleOpenInOpportunities = () => {
    const opportunityId = getOpportunityId();
    
    if (opportunityId) {
      // Navigate directly to opportunity detail page
      navigate(`/opportunities/${opportunityId}`);
    } else {
      // Fallback: navigate to opportunities list
      navigate('/opportunities');
    }
    
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-lg bg-[#1A1B1E] border-l border-[rgba(255,255,255,0.1)] shadow-2xl z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.1)] flex items-start justify-between">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle size={18} className="text-[#F59E0B]" />
                  <span className="text-xs font-medium text-[#F59E0B] uppercase tracking-wider">AI Opportunity</span>
                </div>
                <h2 className="text-xl font-bold text-white">{opportunity.title}</h2>
                {opportunity.category && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs rounded bg-[rgba(0,229,192,0.2)] text-[#00E5C0] border border-[rgba(0,229,192,0.3)]">
                    {opportunity.category}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
              >
                <X size={20} className="text-[#A0AEC0]" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Description */}
              {opportunity.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-[#A0AEC0] mb-2">Description</h3>
                  <p className="text-white leading-relaxed">{opportunity.description}</p>
                </div>
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Priority Score */}
                <div className="glass rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={16} className="text-[#6366F1]" />
                    <span className="text-xs text-[#A0AEC0]">Priority Score</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white">
                      {opportunity.priority_score ?? '—'}
                    </span>
                    {opportunity.priority_score && (
                      <span className={`px-2 py-0.5 text-xs rounded border ${getPriorityColor(opportunity.priority_score)}`}>
                        {opportunity.priority_score >= 70 ? 'High' : opportunity.priority_score >= 40 ? 'Medium' : 'Low'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Impact */}
                <div className="glass rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendUp size={16} className="text-green-400" />
                    <span className="text-xs text-[#A0AEC0]">Impact</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white">
                      {opportunity.impact ?? '—'}
                    </span>
                    {opportunity.impact && (
                      <span className="text-sm text-[#A0AEC0]">/ 10</span>
                    )}
                  </div>
                  {opportunity.impact && (
                    <span className="text-xs text-[#718096]">{getScoreLabel(opportunity.impact)}</span>
                  )}
                </div>

                {/* Effort */}
                <div className="glass rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-amber-400" />
                    <span className="text-xs text-[#A0AEC0]">Effort</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white">
                      {opportunity.effort ?? '—'}
                    </span>
                    {opportunity.effort && (
                      <span className="text-sm text-[#A0AEC0]">/ 10</span>
                    )}
                  </div>
                  {opportunity.effort && (
                    <span className="text-xs text-[#718096]">{getScoreLabel(opportunity.effort)}</span>
                  )}
                </div>

                {/* ROI - Fixed to handle object types */}
                <div className="glass rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CurrencyDollar size={16} className="text-emerald-400" />
                    <span className="text-xs text-[#A0AEC0]">ROI Estimate</span>
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {formatROI(opportunity.roi)}
                  </span>
                </div>
              </div>

              {/* Savings Section */}
              {(opportunity.monthly_savings || opportunity.cost_savings_monthly || 
                opportunity.annual_savings || opportunity.cost_savings_annual ||
                opportunity.hours_saved_monthly || opportunity.time_saved) && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-[#A0AEC0] mb-3">Estimated Savings</h3>
                  <div className="glass rounded-lg p-4 space-y-3">
                    {(opportunity.monthly_savings || opportunity.cost_savings_monthly) && (
                      <div className="flex justify-between items-center">
                        <span className="text-[#A0AEC0]">Monthly Savings</span>
                        <span className="text-lg font-semibold text-emerald-400">
                          {formatCurrency(opportunity.monthly_savings || opportunity.cost_savings_monthly)}
                        </span>
                      </div>
                    )}
                    {(opportunity.annual_savings || opportunity.cost_savings_annual) && (
                      <div className="flex justify-between items-center">
                        <span className="text-[#A0AEC0]">Annual Savings</span>
                        <span className="text-lg font-semibold text-emerald-400">
                          {formatCurrency(opportunity.annual_savings || opportunity.cost_savings_annual)}
                        </span>
                      </div>
                    )}
                    {(opportunity.hours_saved_monthly || opportunity.time_saved) && (
                      <div className="flex justify-between items-center">
                        <span className="text-[#A0AEC0]">Time Saved</span>
                        <span className="text-lg font-semibold text-blue-400">
                          {opportunity.hours_saved_monthly || opportunity.time_saved} hrs/month
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status */}
              {opportunity.status && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-[#A0AEC0] mb-2">Status</h3>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    opportunity.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    opportunity.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    opportunity.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {opportunity.status.charAt(0).toUpperCase() + opportunity.status.slice(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.1)] space-y-3">
              {/* Primary CTA - Fixed route */}
              <button
                onClick={handleProceedToAgent}
                className="w-full py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <Robot size={18} />
                Proceed to Agent Design
                <ArrowRight size={18} />
              </button>
              
              {/* Secondary CTA - Fixed to deep link */}
              <button
                onClick={handleOpenInOpportunities}
                className="w-full py-3 glass text-white rounded-lg font-medium hover:bg-[rgba(255,255,255,0.1)] transition-colors flex items-center justify-center gap-2"
              >
                <ArrowSquareOut size={16} />
                Open in Opportunities
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OpportunityDetailDrawer;
